import React, {Fragment, useState} from "react";
import {useForm, Controller} from "react-hook-form";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import RadioGroup from "@mui/material/RadioGroup";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputLabel from '@mui/material/InputLabel';

import Checkbox from "@mui/material/Checkbox";
import Radio from "@mui/material/Radio";
import TextField from "@mui/material/TextField";
import Alert from '@mui/material/Alert';
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import Button from "@mui/material/Button";
import Snackbar from '@mui/material/Snackbar';
import DatePicker from '@mui/lab/DatePicker';
import TimePicker from "@mui/lab/TimePicker";
import Select from '@mui/material/Select';
import MenuItem from "@mui/material/MenuItem";
import FormHelperText from '@mui/material/FormHelperText';

import * as yup from "yup";
import {yupResolver} from "@hookform/resolvers/yup/dist/yup";

import EvvService from "../../services/evv.service";
import Loading from "../common/Loading";
import SigPadModal from "../common/SigPadModal";

// Initializing the date of submission not less than current year
const minDate = new Date(new Date().getFullYear(), 0, 1);
const dutySchema = yup.object({name: yup.string(), value: yup.boolean()});

const schema = yup.object({
    evvType: yup.mixed().oneOf(['missedIn', 'missedOut', 'missedInOut', 'others']).defined(),
    evvDate: yup.date()
        .min(minDate, "Date must fall in this year!")
        .required("Date is required!"),
    inTime: yup.date().required(),
    outTime: yup.date().required(),
    consumerName: yup.string()
        .required("Consumer name is required!")
        .min(1, "Name is too short!")
        .max(100, "Name is too long."),
    missedReason: yup.string()
        .min(5, "Reason is too short!")
        .max(150, "Reason is too long.")
        .required("Reason is required!"),
    empName: yup.string()
        .required("Employee name is required!")
        .min(1, "Name is too short!")
        .max(100, "Name is too long."),
    duties: yup.array().of(dutySchema),
    disclaimer: yup.boolean().required("Your disclaimer is required!"),
}).required();

const EvvForm = ({applyFor}) => {
    const [showEmpSigPad, setShowEmpSigPad] = useState(false);
    const [showConsumerSigPad, setShowConsumerSigPad] = useState(false);
    const [empSig, setEmpSig] = useState(null);
    const [consumerSig, setConsumerSig] = useState(null);
    const [error, setError] = useState(null);

    const handleEmpCloseModal = () => {
        setShowEmpSigPad(false);
    }

    const handleConsumerCloseModal = () => {
        setShowConsumerSigPad(false);
    }

    const getEmpSignature = (sigData) => {
        setEmpSig(sigData);
    }
    const getConsumerSignature = (sigData) => {
        setConsumerSig(sigData);
    }

    const [showSnackbar, setShowSnackbar] = React.useState(false);
    const formState = {
        evvType: "missedIn",
        evvDate: new Date(),
        inTime: new Date(),
        outTime: new Date(),
        consumerName: "",
        missedReason: "",
        empName: applyFor?.empName ? applyFor.empName : "",
        duties: [
            {key: "115_Meal_Preparation", value: false},
            {key: "116_Housework_Chore", value: false},
            {key: "117_managing_finance", value: false},
            {key: "118_managing_medications", value: false},
            {key: "119_shopping", value: false},
            {key: "120_transportation", value: false},
            {key: "122_hygiene", value: false},
            {key: "123_dressing_upper", value: false},
            {key: "124_dressing_lower", value: false},
            {key: "125_locomotion", value: false},
            {key: "126_transfer", value: false},
            {key: "127_toilet_use", value: false},
            {key: "128_bed_mobility", value: false},
            {key: "129_feeding", value: false},
            {key: "134_bathing", value: false},
            {key: "137_lotion_ointment", value: false},
            {key: "138_laundry", value: false},
            {key: "140_supervision_coaching", value: false},
            {key: "141_incontinence_care", value: false},
            {key: "203_other", value: false}],
        disclaimer: true
    }
    const [submitted, setSubmitted] = React.useState(false);
    const {handleSubmit, control, reset, setValue, getValues, formState: {errors}} = useForm({
        defaultValues: formState,
        resolver: yupResolver(schema)
    });

    const handleDutyChange = (event) => {
        const target = event.target;
        const copyDuty = getValues("duties");
        const filtered = copyDuty.filter((duty) => {
            if (duty.key === target.name) {
                duty.value = target.checked;
                return true;
            }
            return true;
        });
        setValue("duties", filtered);
    }

    // const handleDisclaimerChange = (event) => {
    //     const target = event.target;
    //     const currentDisclaimer = getValues("disclaimer");
    //
    //     setValue("disclaimer", !currentDisclaimer)
    // }

    const onSubmit = async (data) => {
        console.log(data);
        if (consumerSig != null && empSig != null) {
            setError(null);
            setSubmitted(true);
            // track the employee submissions
            data.empId = applyFor.uid;
            const randomId = new Date().getTime();
            try {
                const clientSigRef = await EvvService.uploadSignature("client_" + randomId, consumerSig);
                const empSigRef = await EvvService.uploadSignature("emp_" + randomId, empSig);
                data.clientSignature = clientSigRef.ref.fullPath;
                data.empSignature = empSigRef.ref.fullPath;
                await EvvService.create(data);
                setShowSnackbar(true);
                reset(formState);
            } catch (e) {
                console.log(e);
                reset(formState)
            }
        } else {
            setError({message: "Either consumer or employee signature is missing!"});
        }
    }
    return (
        <Fragment>
            <Snackbar
                open={showSnackbar}
                autoHideDuration={6000}
                onClose={() => setShowSnackbar(false)}
                anchorOrigin={{vertical: "top", horizontal: "center"}}
                message="Missed Evv submitted Successfully!"
            />
            <Grid container spacing={2}>
                <Grid item sm={12} md={12}>
                    {!applyFor && <Typography component="h3" variant="h6" sx={{color: "red", mt:2}}>
                        Employee information is missing! Contact Administrator!
                    </Typography>}
                    <SigPadModal
                        open={showEmpSigPad}
                        handleClose={handleEmpCloseModal}
                        getSignature={getEmpSignature}
                    />
                    <SigPadModal
                        open={showConsumerSigPad}
                        handleClose={handleConsumerCloseModal}
                        getSignature={getConsumerSignature}
                    />
                    {applyFor &&
                        <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate sx={{mt: 1}}>
                            {error && <Alert severity="error">{error.message}</Alert>}
                            <FormControl sx={{mb: 2, mt: 2}}>
                                <FormLabel>Select Missed Type</FormLabel>
                                <Controller
                                    name="evvType"
                                    control={control}
                                    render={({field}) =>
                                        <RadioGroup
                                            row
                                            {...field}
                                        >
                                            <FormControlLabel
                                                value="missedIn"
                                                control={<Radio/>}
                                                label="Missed In"
                                            />
                                            <FormControlLabel
                                                value="missedOut"
                                                control={<Radio/>}
                                                label="Missed Out"
                                            />
                                            <FormControlLabel
                                                value="missedInOut"
                                                control={<Radio/>}
                                                label="Missed In and Out"
                                            />
                                            <FormControlLabel
                                                value="other"
                                                control={<Radio/>}
                                                label="Other"
                                            />
                                        </RadioGroup>
                                    }
                                />
                            </FormControl>

                            <Stack
                                direction={{xs: "column", sm: "row"}}
                                divider={<Divider orientation="vertical" flexItem/>}
                                spacing={{xs: 1, sm: 2, md: 4}}
                                sx={{mt: 1}}
                            >
                                <FormControl fullWidth>
                                    <Controller
                                        name="evvDate"
                                        control={control}
                                        render={({field}) =>
                                            <DatePicker
                                                label="Date"
                                                inputFormat="MM/dd/yyyy"
                                                {...field}
                                                minDate={minDate}
                                                disableFuture={true}
                                                renderInput={(params) => <TextField
                                                    {...params}
                                                    error={!!errors.evvDate}
                                                    helperText={errors.evvDate && errors.evvDate.message}
                                                />}
                                            />}
                                    />
                                </FormControl>
                                <FormControl fullWidth>
                                    <Controller
                                        name="inTime"
                                        control={control}
                                        render={({field}) =>
                                            <TimePicker
                                                label="In time"
                                                {...field}
                                                renderInput={(params) => <TextField
                                                    error={!!errors.inTime}
                                                    helperText={errors.inTime && errors.inTime.message}
                                                    {...params} />}
                                            />
                                        }
                                    />
                                </FormControl>
                                <FormControl fullWidth>
                                    <Controller
                                        name="outTime"
                                        control={control}
                                        render={({field}) =>
                                            <TimePicker
                                                label="Out time"
                                                {...field}
                                                renderInput={(params) => <TextField
                                                    error={!!errors.outTime}
                                                    helperText={errors.outTime && errors.outTime.message}
                                                    {...params} />}
                                            />
                                        }
                                    />
                                </FormControl>
                            </Stack>
                            <FormControl fullWidth sx={{mt: 2}}>
                                <InputLabel id="consumerNameControl">Select Consumer </InputLabel>
                                <Controller
                                    name="consumerName"
                                    control={control}
                                    render={({field}) =>
                                        <Select
                                            label="Consumer Name"
                                            {...field}
                                            labelId="consumerNameControl"
                                            id="consumerNameControl"
                                            error={!!errors.consumerName}
                                        >
                                            {applyFor?.clients && applyFor.clients.map((item, key) =>
                                                <MenuItem value={item} key={key}>{item}</MenuItem>)}
                                        </Select>
                                    }
                                />
                            </FormControl>
                            <FormControl fullWidth sx={{mt: 2}}>
                                <Controller
                                    name="missedReason"
                                    control={control}
                                    render={({field}) =>
                                        <TextField
                                            {...field}
                                            fullWidth
                                            multiline
                                            rows={2}
                                            label="Enter missed reason"
                                            variant="outlined"
                                            error={!!errors.missedReason}
                                            helperText={errors.missedReason && errors.missedReason.message}
                                        />
                                    }
                                />
                            </FormControl>
                            <FormControl>
                                <Typography variant="subtitle1">Consumer Signature</Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        '& > :not(style)': {
                                            m: 1,
                                            width: 120,
                                            height: 80,
                                        },
                                        border: 1,
                                        p: 1,
                                    }}
                                >
                                    {consumerSig &&
                                        <React.Fragment>
                                            <img src={consumerSig} alt="Consumer Signature" width="350px"
                                                 height="350px"/>
                                        </React.Fragment>
                                    }
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={(e) => setShowConsumerSigPad(true)}
                                    >Sign here </Button>
                                </Box>
                            </FormControl>
                            <Box sx={{flexGrow: 1, mt: 1, p: "1rem", bgcolor: "#ecf7eb"}}>
                                <Grid
                                    container
                                    spacing={{xs: 1, md: 2}}
                                    columns={{xs: 4, sm: 8, md: 12}}
                                >

                                    <Controller
                                        name="duties"
                                        control={control}
                                        render={({field}) => (
                                            getValues("duties").map((obj) => (
                                                    <Grid item xs={4} sm={4} md={4} key={obj.key}>
                                                        <FormControlLabel
                                                            {...field}
                                                            control={<Checkbox
                                                                size="small"
                                                                name={obj.name}
                                                                checked={obj.value}
                                                                color="primary"
                                                                onChange={handleDutyChange}
                                                            />}
                                                            label={obj.key.toUpperCase()}
                                                            name={obj.key}
                                                        />
                                                    </Grid>
                                                )
                                            )
                                        )}
                                    />

                                </Grid>
                            </Box>
                            <FormControl fullWidth sx={{mt: 2}}>
                                <Controller
                                    name="empName"
                                    control={control}
                                    render={({field}) =>
                                        <TextField
                                            {...field}
                                            fullWidth
                                            label="Enter Employee name"
                                            variant="outlined"
                                            error={!!errors.empName}
                                            helperText={errors.empName && errors.empName.message}
                                        />
                                    }
                                />
                            </FormControl>

                            <FormControl sx={{mt: 2}}>
                                <Typography variant="subtitle1">Employee Signature</Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        '& > :not(style)': {
                                            m: 1,
                                            width: 120,
                                            height: 80,
                                        },
                                        border: 1,
                                        p: 1,
                                    }}
                                >
                                    {empSig && <img src={empSig} alt={"emp Signature"}/>}
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={(e) => setShowEmpSigPad(true)}
                                    >Sign here </Button>
                                </Box>
                            </FormControl>
                            <FormControl fullWidth sx={{mt: 2}}>
                                <Controller
                                    name="disclaimer"
                                    control={control}
                                    render={({field}) =>
                                        <FormControlLabel
                                            {...field}
                                            control={<Checkbox
                                                name={field.name}
                                                checked={field.value}
                                                onChange={field.onChange}
                                                color="primary"/>}
                                            label="I agree that the information entered above is accurate based on my knowledge."
                                        />
                                    }
                                />
                                <FormHelperText>You must accept the disclaimer.</FormHelperText>
                            </FormControl>
                            <Button
                                type="submit"
                                variant="contained"
                                sx={{mt: 3, mb: 2}}
                                disabled={submitted}
                            >
                                Submit
                            </Button>
                        </Box>
                    }
                </Grid>
            </Grid>
        </Fragment>
    );
};

export default EvvForm;
